import { DEAL_CLOSED_STATUSES } from '../../constants';
import { insertProspectFromDeal } from '../../services/clients';
import { insertDealCreatedStatus } from '../../services/dealActivities';
import { insertDeal } from '../../services/deals';
import { notify } from '../../store/feedback';
import { dealSaveProblems } from '../../utils/budget';
import { uid } from '../../utils/format';
import { becomesWon, dealRow, manualStatusDates, statusDatesPatch } from './dealDefaults';
import { blankDealLine } from './dealLineDefaults';
import { syncPhaseAmounts } from './phaseAmountsSync';

/* Fiche d'une affaire : enregistrement (avec création du prospect saisi à la volée). */
export default function dealFormHandlers(deps, api) {
  const { appState, dealModal, dealNewProspect, deals, loadAppData, saveDealLines, session } = deps;
  const { setDealModal, setDealNewProspect, setCrmClients } = deps;

  // Prospect créé à la volée (bouton "+") : on ne le crée en base qu'à l'enregistrement de
  // l'affaire, pour ne pas laisser de fiche contact orpheline si l'utilisateur annule.
  // Renvoie l'id du client de l'affaire, ou undefined si la création a échoué.
  async function clientIdFor(m) {
    if (!dealNewProspect) return m.client_id || null;
    const n = dealNewProspect;
    const newClientId = uid();
    const { error } = await insertProspectFromDeal(n, newClientId);
    if (error) return void notify('Erreur création du prospect : ' + error.message);
    const prospect = {
      id: newClientId,
      nom: n.nom.trim(),
      statut: 'prospect',
      secteurs: n.secteurs || [],
    };
    setCrmClients((prev) => [...prev, prospect]);
    return newClientId;
  }

  // Lignes refusées par la base (colonne manquante, contrainte…) : la fiche reste ouverte avec la
  // saisie, pour corriger puis enregistrer à nouveau, au lieu de se fermer comme si tout était fait.
  function notifyLinesError(error) {
    notify(
      `Lignes de l'affaire non enregistrées : ${error.message}\nLa fiche reste ouverte : réessaie après correction.`,
    );
  }

  async function saveDealModal() {
    const m = dealModal;
    if (!m.nom || !m.nom.trim()) return void notify("Le nom de l'affaire est requis.");
    if (dealNewProspect && (!dealNewProspect.nom || !dealNewProspect.nom.trim()))
      return void notify('Nom du prospect requis.');
    // Motif demandé à chaque clôture, comme sur le kanban : sans lui, le bloc « Motifs de perte »
    // du dashboard ne se remplit pas.
    const prevStatus = m.id ? deals.find((a) => a.id === m.id)?.statut : null;
    if (
      DEAL_CLOSED_STATUSES.includes(m.statut) &&
      m.statut !== prevStatus &&
      !(m.motif_perte || '').trim()
    )
      return void notify('Choisis un motif de clôture.');
    const lignes = m.lignes && m.lignes.length ? m.lignes : [blankDealLine()];
    // Écarts bloquants à tout statut (engagement ≠ dates, budget ≠ jours × TJM) ; en statut gagné
    // (Devis accepté, En cours, Terminé), lignes vendues complètes exigées en plus.
    const problemes = dealSaveProblems(lignes, m.statut);
    if (problemes.length) return void setDealModal((o) => ({ ...o, _gateError: problemes }));
    const clientId = await clientIdFor(m);
    if (clientId === undefined) return;
    const row = dealRow(m, clientId);
    if (m.id) {
      const prev = deals.find((a) => a.id === m.id);
      if (prev && prev.statut !== row.statut) {
        await insertDealCreatedStatus(m, row, session);
        Object.assign(row, statusDatesPatch(prev, row.statut));
      }
      Object.assign(row, manualStatusDates(m));
      if ((await api.saveDealPatch(m.id, row))?.error) return;
      const linesError = await saveDealLines(m.id, lignes);
      if (linesError) return void notifyLinesError(linesError);
      // Affaire convertie : le budget se pilote depuis le CRM, les phases reprennent ses montants.
      if (m.converted_client_id && (await syncPhaseAmounts(lignes, appState?.clients)))
        await loadAppData?.();
    } else {
      const maxOrdre = deals.reduce((mx, a) => Math.max(mx, a.ordre || 0), 0);
      const id = uid();
      Object.assign(row, statusDatesPatch(null, row.statut), manualStatusDates(m));
      const insertError = (await insertDeal(id, maxOrdre, row))?.error;
      if (insertError) return void notify('Affaire non créée : ' + insertError.message);
      // Statut de création tracé dans l'historique, comme un changement de statut.
      await insertDealCreatedStatus({ id }, row, session);
      if (becomesWon(null, row.statut) && clientId)
        await api.saveContactPatch(clientId, { statut: 'actif' });
      const linesError = await saveDealLines(id, lignes);
      if (linesError) {
        // L'affaire (et son prospect) existe déjà : la fiche les garde, pour qu'un nouvel essai
        // mette l'affaire à jour au lieu d'en créer une seconde.
        setDealModal((o) => ({ ...o, id, client_id: clientId }));
        setDealNewProspect(null);
        api.loadDeals();
        return void notifyLinesError(linesError);
      }
    }
    setDealModal(null);
    setDealNewProspect(null);
    api.loadDeals();
  }

  return { saveDealModal };
}
